import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from "react-intl";
import {Dialog, IconButton, Stack} from "@mui/material";
import {styled} from "@mui/material/styles";

import CustomButton from "components/CustomButton";
import CloseCrossIcon from "components/icons/CloseCrossIcon";
import SparkleIcon from "components/icons/SparkleIcon";
import {ReactComponent as CloseIcon} from "assets/img/close.svg";
import {black500, greyishBlue100, red600} from "assets/jss/main";

import {fieldFor} from "../attributeField";
import {targetLabel} from "../enrichmentConstants";

import TargetEditor from "./TargetEditor";

/**
 * "Optimisation des attributs": every target of the row, one line each. A line with a
 * value (the AI suggestion or a value chosen here) is validated or dismissed on its own;
 * a line without one can only leave the suggestions. A decided line leaves the dialog.
 * On the enriched state, a line changes the applied value directly.
 */
AttributesDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    state: PropTypes.oneOf(['missing', 'pending', 'applied']).isRequired,
    targets: PropTypes.arrayOf(PropTypes.string).isRequired,
    catalog: PropTypes.object.isRequired,
    initialValues: PropTypes.object.isRequired,
    suggestedValues: PropTypes.object.isRequired,
    deciding: PropTypes.bool,
    onApply: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onChangeApplied: PropTypes.func.isRequired,
};

const Header = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: `1px solid ${greyishBlue100}`,
});

const Title = styled('span')({
    fontSize: '20px',
    fontWeight: 600,
    color: black500,
});

const Body = styled('div')({
    flex: '1 1 auto',
    overflowY: 'auto',
    padding: '16px 24px',
});

const Footer = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: `1px solid ${greyishBlue100}`,
});

const Label = styled('span')({
    fontSize: '12px',
    fontWeight: 600,
    color: black500,
});

const Actions = styled('div')({
    display: 'flex',
    gap: '8px',
    width: '176px',
    flexShrink: 0,
});

const squareButtonSx = {minWidth: '40px', width: '40px', height: '40px', padding: 0, '& svg': {width: '16px', height: '16px'}, '& svg path': {fill: red600}};

const hasValue = (value) => (Array.isArray(value) ? value.length > 0 : null !== value && undefined !== value && '' !== value);

function AttributesDialog({open, onClose, state, targets, catalog, initialValues, suggestedValues, deciding = false, onApply, onDismiss, onChangeApplied}) {
    const intl = useIntl();
    const [values, setValues] = useState(initialValues);
    const [resets, setResets] = useState({});

    useEffect(() => {
        if (open) {
            setValues(initialValues);
        }
    }, [open]);

    const decidable = 'applied' !== state;
    const filled = targets.filter((target) => hasValue(values[target]));
    const valuesOf = (keys) => Object.fromEntries(keys.map((target) => [target, values[target]]));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{sx: {width: '992px', maxWidth: 'calc(100% - 64px)', height: '640px', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}}
        >
            <Header>
                <Stack direction="row" alignItems="center" gap="4px">
                    <SparkleIcon size={16}/>
                    <Title><FormattedMessage id="enrichment.attributes.dialog.title" values={{count: targets.length}}/></Title>
                </Stack>
                <IconButton aria-label={intl.formatMessage({id: 'enrichment.attributes.dialog.close'})} onClick={onClose} sx={{padding: 0}}>
                    <CloseCrossIcon size={24}/>
                </IconButton>
            </Header>
            <Body>
                <Stack gap="12px">
                    {targets.map((target) => (
                        <Stack key={target} direction="row" alignItems="flex-end" gap="32px">
                            <Stack flex="1 0 0" minWidth={0} gap="4px">
                                <Label>{targetLabel(intl, catalog, target)}</Label>
                                <Stack direction="row">
                                    <TargetEditor
                                        key={resets[target] ?? 0}
                                        target={target}
                                        field={fieldFor(catalog, target)}
                                        value={values[target]}
                                        suggestedValue={suggestedValues[target] ?? null}
                                        highlighted={decidable && !hasValue(values[target])}
                                        disabled={deciding}
                                        onChange={(value) => {
                                            const previous = values[target];
                                            setValues((current) => ({...current, [target]: value}));
                                            if (!decidable) {
                                                onChangeApplied(target, value).then((saved) => {
                                                    if (!saved) {
                                                        setValues((current) => ({...current, [target]: previous}));
                                                        setResets((current) => ({...current, [target]: (current[target] ?? 0) + 1}));
                                                    }
                                                });
                                            }
                                        }}
                                    />
                                </Stack>
                            </Stack>
                            {decidable && (
                                <Actions>
                                    {hasValue(values[target]) ? (
                                        <>
                                            <CustomButton variant="contained" color="green" fullWidth disabled={deciding} onClick={() => onApply(valuesOf([target]))}>
                                                <FormattedMessage id="enrichment.action.apply"/>
                                            </CustomButton>
                                            <CustomButton variant="contained" color="lightRed" sx={squareButtonSx} disabled={deciding} aria-label={intl.formatMessage({id: 'enrichment.action.dismissTarget'}, {target: targetLabel(intl, catalog, target)})} onClick={() => onDismiss([target])}>
                                                <CloseIcon/>
                                            </CustomButton>
                                        </>
                                    ) : (
                                        <CustomButton variant="contained" color="lightRed" fullWidth disabled={deciding} onClick={() => onDismiss([target])}>
                                            <FormattedMessage id="enrichment.action.dismissSelection"/>
                                        </CustomButton>
                                    )}
                                </Actions>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Body>
            {decidable && (
                <Footer>
                    <CustomButton variant="contained" color="lightRed" disabled={deciding || 0 === targets.length} onClick={() => onDismiss(targets)}>
                        <FormattedMessage id="enrichment.attributes.dialog.dismissAll"/>
                    </CustomButton>
                    <CustomButton variant="contained" color="blue" loading={deciding} disabled={deciding || 0 === filled.length} onClick={() => onApply(valuesOf(filled))}>
                        {0 === filled.length
                            ? <FormattedMessage id="enrichment.attributes.dialog.applyAll"/>
                            : <FormattedMessage id="enrichment.attributes.dialog.applyAllCount" values={{count: filled.length}}/>}
                    </CustomButton>
                </Footer>
            )}
        </Dialog>
    );
}

export default AttributesDialog;
